/* eslint-disable react-hooks/exhaustive-deps */
/**
 * A React functional component that handles artist management.
 * It utilizes various hooks from the React library such as useEffect, useRef, useState, and useTranslation.
 * It also uses the useDispatch function from the react-redux library.
 * Additionally, it imports the useWindowDimension hook from a custom component and the showToast function from the toastSlice feature of the store.
 * It imports the artist data from the artist.json file and the debounce function from the lodash library.
 * It renders the ArtistManagementComponent component.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { debounce } from "lodash";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import useWindowDimension from "../../components/hooks/useWindowDimension";
import { uploadFile } from "../../helpers/helper.service";
import {
  artistListResponce,
  createArtistResponce,
  deleteArtistResponce,
  updateArtistResponce,
} from "../../store/features/artists/artistsSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import ArtistManagementComponent from "./artistManagementComponent";

/**
 * Represents the container component for managing artists.
 * @param {Object} props - The component props.
 * @returns The rendered ArtistManagementContainer component.
 */
const ArtistManagementContainer = (props) => {
  /**
   * This component defines multiple state variables and initializes them with their initial values.
   * It also initializes some refs and hooks.
   * @returns None
   */
  const { t } = useTranslation();
  let triggerRef = useRef(null);
  const smarttableref = useRef();
  const dispatch = useDispatch();
  const dimensions = useWindowDimension();
  const pageSizeRef = useRef(15);
  const tableContainerRef = useRef(null);
  const artistList = useSelector((state) => state?.artist?.artistResponce);
  const [loading, setLoading] = useState(false);
  const [modalName, setModalName] = useState({ modal: "add" });
  const [aboutTouched, setAboutTouched] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [showCreateArtistModal, setShowCreateArtistModal] = useState(false);
  const [filteredEventData, setFilteredEventData] = useState([]);
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [showDeleteUserModal, setshowDeleteUserModal] = useState(false);
  const [updateartistValues, setUpdateartistValues] = useState(null);
  const [artistValue, setArtistValue] = useState("");
  const [artistprofileValue, setArtistprofileValue] = useState("");
  const [deleteArtistItem, setDeleteArtistItem] = useState();
  const [aboutTextEditorValue, setAboutTextEditorValue] = useState();
  const [uploadedDocumentThumnailFile, setuploadedDocumentThumnailFile] =
    useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [profileImage, setProfileImage] = useState();
  const [artisttextcount, setArtisttextcount] = useState(
    modalName === "add" ? 0 : artistValue?.length
  );
  const [updateArtistId, setUpdateArtistId] = useState();
  const [instagramUrlValue, setInstagramUrlValue] = useState("");
  const [instagramUrlBaseline, setInstagramUrlBaseline] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  useEffect(() => {
    if (Array.isArray(artistList)) {
      const lowercaseSearchValue = searchValue?.toLowerCase();

      const filteredData = artistList?.filter(
        (item) => item?.name?.toLowerCase()?.includes(lowercaseSearchValue)
        // || item?.bio?.toLowerCase()?.includes(lowercaseSearchValue)
      );

      setFilteredEventData(filteredData);
    }
  }, [searchValue, artistList]);

  /**
   * Initialize state variables for loader HTML height, table filter height, tab header height, and header
   * @returns None
   */
  const [loaderHtml, setLoaderHtmlHeight] = useState("");
  const [tabHeaderHeight, setTabHeaderHeight] = useState("");
  /**
   * Initializes state variables for managing the visibility and data of artist model loader buttons.
   * @returns None
   */
  const [iscreateArtistModelLoaderButton, setIscreateArtistModelLoaderButton] =
    useState(false);

  const artistlength = artistList?.length;
  const [placeHolder, setPlaceHolder] = useState(true);
  useEffect(() => {
    getList();
  }, []);

  /**
   * Closes the delete user modal by setting the `showDeleteUserModal` state to `false`.
   * @returns None
   */
  const closeDeleteUserModal = () => setshowDeleteUserModal(false);

  /**
   * Retrieves the artist list by dispatching the artistListResponce action.
   * If the action is fulfilled, sets the placeholder to false.
   * If the action is rejected, logs the resultAction to the console.
   * @returns None
   */
  const getList = () => {
    dispatch(artistListResponce()).then((resultAction) => {
      if (artistListResponce.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (artistListResponce.rejected.match(resultAction)) {
        setPlaceHolder(false);
      }
    });
  };

  /**
   * Opens the delete user modal and sets the state to show the modal.
   * @param {Event} e - The event object.
   * @param {Object} item - The item to be deleted.
   * @returns None
   */
  const openDeleteUserModal = (e, item) => {
    setshowDeleteUserModal(true);
  };

  /**
   * Updates the search value based on the filter text entered by the user.
   * @param {{string}} filterText - The filter text entered by the user.
   * @returns None
   */
  const onTableFilterChange = (filterText) => {
    const filteredData = artistList?.filter((item) =>
      item?.name?.toLowerCase().includes(filterText?.toLowerCase())
    );

    setSearchValue(filterText);
    // Set the filtered data to update the table
    setFilteredData(filteredData);
  };
  const searchFilterClear = () => {
    smarttableref.current.querySelectorAll("button")[1].click();
  };

  /**
   * Clears the search input by programmatically clicking the clear search button.
   * @returns None
   */
  const ClearSearchButton = () => {
    smarttableref.current.querySelectorAll("button")[0].click();
  };
  /**
   * An array of column objects used for displaying data in a table.
   * Each column object contains a label, key, and additional properties.
   * @type {Array<Object>}
   */
  const columns = [
    {
      label: (
        <>
          {t("ARTISTS")}{" "}
          <span className="thCount">
            {" "}
            {searchValue === null ? artistlength : filteredEventData?.length}
          </span>
        </>
      ),
      key: "name",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("ABOUT_ARTIST")}</>,
      key: "bio",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("No_OF_FOLOWERS")}</>,
      key: "followers",
      filter: false,
      sorting: false,
    },
    {
      label: <>{t("ACTIONS")}</>,
      key: "status",
      filter: false,
      sorting: false,
    },
  ];

  /**
   * Deletes a user by dispatching a deleteArtistResponce action and handling the result.
   * @returns None
   */
  const onDeleteUser = async () => {
    setDeleteLoader(true);
    dispatch(deleteArtistResponce(deleteArtistItem?._id)).then(
      (resultAction) => {
        if (deleteArtistResponce.fulfilled.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          closeDeleteUserModal();
          setDeleteLoader(false);
        } else if (deleteArtistResponce.rejected.match(resultAction)) {
          closeDeleteUserModal();
          setDeleteLoader(false);
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
        }
      }
    );
  };

  /**
   * Opens the create artist modal.
   * - Sets the modal name to 'add'.
   * - Sets the create artist loader button to true.
   * - Sets a timeout to show the create artist modal after 1 second.
   * - Sets the create artist loader button to false.
   * @returns None
   */
  const openCreateArtistModal = () => {
    setModalName({ modal: "add" });
    setAboutTextEditorValue("");
    setInstagramUrlValue("");
    setInstagramUrlBaseline("");
    setTimeout(() => {
      setShowCreateArtistModal(true);
    }, 1000);
  };

  /**
   * Closes the create artist modal and resets the artist text count.
   * @returns None
   */
  const closeCreateArtistModal = () => {
    setAboutTouched(false);
    setuploadedDocumentThumnailFile("");
    setProfileImage("");
    setInstagramUrlValue("");
    setInstagramUrlBaseline("");
    setShowCreateArtistModal(false);
    setArtisttextcount(0);
  };

  const handleUploadThumnailFile = (files) => {
    if (files && files?.length > 0) {
      setuploadedDocumentThumnailFile(files);
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        setProfileImage(event?.target?.result);
      });
      reader.readAsDataURL(files[0]);
    } else {
      setuploadedDocumentThumnailFile(null);
      setProfileImage(null);
    }
  };

  /**
   * Clears the uploaded thumbnail file, profile image, and resets the dimension error flag.
   * @returns None
   */
  const handleClearUploadedThumnailFile = () => {
    setuploadedDocumentThumnailFile("");
    setProfileImage("");
    // setDimenstionError(false);
  };

  /**
   * Clears the create data by resetting the artist text count and resetting the values of the create form.
   * @returns None
   */
  const clearCreatData = () => {
    setArtisttextcount(0);
  };
  /**
   * Loads more items when triggered, with a delay of 2 seconds.
   * @returns None
   */
  const loadMoreItems = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      pageSizeRef.current += 10;
    }, 2000);
  };

  /**
   * Debounces the handleTableScroll function to prevent it from being called too frequently.
   * When the table container is scrolled, it checks if the scroll position is at the bottom
   * and if there are more items to load. If both conditions are met, it calls the loadMoreItems function.
   * @returns None
   */
  const handleTableScroll = debounce(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const isAtBottom =
        tableContainer.scrollTop + tableContainer.clientHeight >=
        tableContainer.scrollHeight;
      if (isAtBottom && artistList?.length > pageSizeRef.current) {
        loadMoreItems();
      }
    }
  }, 200);

  /**
   * Adds an event listener to the table container element to handle scroll events.
   * The event listener is removed when the component is unmounted.
   * @param {{function}} handleTableScroll - The function to handle the table scroll event.
   * @returns None
   */
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [handleTableScroll]);

  /**
   * useEffect hook that sets the heights of various elements based on the current state.
   * @param {{boolean}} loading - Indicates whether the page is currently loading.
   * @returns None
   */
  useEffect(() => {
    loading &&
      setLoaderHtmlHeight(
        document.getElementsByClassName("table-responsive")[0].offsetHeight
      );
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0].offsetHeight
    );
    setTabHeaderHeight(document.getElementsByTagName("thead")[0].offsetHeight);
  }, [loading]);

  /**
   * Handles the submission of creating or updating an artist.
   * @param {{object}} data - The data object containing the artist information.
   * @returns None
   */
  const handleSubmitCreate = async (data) => {
    setIscreateArtistModelLoaderButton(true);
    if (modalName?.modal === "add") {
      let url = "";
      try {
        const file = data?.thumbnailfile?.[0];
        if (file instanceof File) {
          url = await uploadFile(file);
        }
      } catch (e) {
        setIscreateArtistModelLoaderButton(false);
        dispatch(
          showToast({
            show: false,
            message: e?.message || "Image upload failed",
            type: "error",
          })
        );
        return;
      }
      const params = {
        artistname: data?.artistname,
        aboutartist: data?.aboutartist,
        thumbnailfile: url,
      };
      const igTrim = (data?.instagramurl || "").trim();
      if (igTrim) params.instagramurl = igTrim;
      setArtisttextcount(0);
      dispatch(createArtistResponce(params)).then((resultAction) => {
        if (createArtistResponce.fulfilled.match(resultAction)) {
          getList();
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          setShowCreateArtistModal(false);
          setIscreateArtistModelLoaderButton(false);
          setAboutTouched(false);
          setTimeout(() => {
            setAboutTextEditorValue("");
            setuploadedDocumentThumnailFile("");
            setArtistprofileValue("");
            setProfileImage("");
          }, 500);
        } else if (createArtistResponce.rejected.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
          setShowCreateArtistModal(false);
          setIscreateArtistModelLoaderButton(false);
          setAboutTouched(false);
          setTimeout(() => {
            setuploadedDocumentThumnailFile("");
            setArtistprofileValue("");
            setProfileImage("");
          }, 500);
        }
      });
    } else {
      let editUrl;
      if (
        typeof data?.thumbnailfile === "string" ||
        data?.thumbnailfile === undefined
      ) {
        editUrl = data?.thumbnailfile;
      } else {
        try {
          const file = data?.thumbnailfile?.[0];
          editUrl =
            file instanceof File ? await uploadFile(file) : data?.thumbnailfile;
        } catch (e) {
          setIscreateArtistModelLoaderButton(false);
          dispatch(
            showToast({
              show: false,
              message: e?.message || "Image upload failed",
              type: "error",
            })
          );
          return;
        }
      }
      // data["id"] = updateArtistId;
      const params = {
        artistname: data?.artistname,
        aboutartist: data?.aboutartist,
        thumbnailfile: editUrl,
        id: updateArtistId,
      };
      const igTrim = (data?.instagramurl || "").trim();
      const prevIg = (instagramUrlBaseline || "").trim();
      if (igTrim) params.instagramurl = igTrim;
      else if (prevIg) params.instagramurl = "";
      dispatch(updateArtistResponce(params)).then((resultAction) => {
        if (updateArtistResponce.fulfilled.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          setShowCreateArtistModal(false);
          setIscreateArtistModelLoaderButton(false);
          setAboutTouched(false);
          setTimeout(() => {
            setuploadedDocumentThumnailFile("");
            setArtistprofileValue("");
            setProfileImage("");
            setAboutTextEditorValue("");
          }, 500);
        } else if (updateArtistResponce.rejected.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
          setShowCreateArtistModal(false);
          setIscreateArtistModelLoaderButton(false);
          setAboutTouched(false);
          setTimeout(() => {
            setuploadedDocumentThumnailFile("");
            setArtistprofileValue("");
            setProfileImage("");
            setAboutTextEditorValue("");
          }, 500);
        }
      });
    }
  };

  /**
   * Updates the artist function with the values from the form.
   * @param {{object}} form - The form object containing the updated values.
   * @returns None
   */
  const updateArtistFunction = (form) => {
    setUpdateartistValues(form);
  };

  /**
   * Handles the change event for the artist text input field.
   * Updates the artist text count state based on the new value.
   * @param {{Event}} e - The change event object.
   * @returns None
   */
  const handleArtisttextChange = (e) => {
    const value = e.target.value;
    setArtisttextcount(value?.length);
  };

  /**
   * Updates the artist information and opens the edit modal.
   * @param {{Event}} e - The event object.
   * @param {{object}} item - The artist item to update.
   * @returns None
   */
  const onUpdateArtist = (e, item) => {
    setUpdateArtistId(item?._id);
    setModalName({ modal: "Edit" });
    setArtistValue(item?.name);
    setArtisttextcount(item?.name?.length);
    setArtistprofileValue(item?.image);
    setAboutTextEditorValue(item?.bio[0]);
    const ig =
      item?.instagramurl ??
      item?.instagramUrl ??
      item?.instagram_url ??
      "";
    setInstagramUrlValue(typeof ig === "string" ? ig : "");
    setInstagramUrlBaseline(typeof ig === "string" ? ig : "");
    // setArtisttextcount(item?.artist?.length)
    setTimeout(() => {
      setShowCreateArtistModal(true);
    }, 1000);
  };

  /**
   * useEffect hook that updates the "Gneresname" value in the "updateartistValues" form
   * with the value of "artistValue" when any of the dependencies change.
   * @param {{Function}} updateartistValues - The function to update the form values.
   * @param {{any}} artistValue - The value to update the "Gneresname" field with.
   * @param {{any}} aboutTextEditorValue - The value of the "aboutTextEditorValue" dependency.
   * @param {{any}} artistprofileValue - The value of the "artistprofileValue" dependency.
   * @returns None
   */
  useEffect(() => {
    if (updateartistValues !== null) {
      updateartistValues.setValue("Gneresname", artistValue, {
        shouldValidate: true,
      });
    }
  }, [
    updateartistValues,
    artistValue,
    aboutTextEditorValue,
    artistprofileValue,
  ]);

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  return (
    <ArtistManagementComponent
      // createArtistFunction={createArtistFunction}
      modalName={modalName}
      createArtistModelLoaderButton={iscreateArtistModelLoaderButton}
      showCreateArtistModal={showCreateArtistModal}
      openCreateArtistModal={openCreateArtistModal}
      showDeleteUserModal={showDeleteUserModal}
      openDeleteUserModal={(e, item) => {
        setDeleteArtistItem(item);
        openDeleteUserModal(e, item);
      }}
      smarttableref={smarttableref}
      closeDeleteUserModal={() => {
        setshowDeleteUserModal(false);
      }}
      onDeleteUser={(e, item) => {
        onDeleteUser(e, item);
      }}
      columns={columns}
      artistData={artistList}
      artisttextcount={artisttextcount}
      ClearSearchButton={ClearSearchButton}
      NoDataFound={t("NO_DATA_FOUND")}
      NoDataTitle={t("NO_DATA_FOUND")}
      NoDataDesc={t(
        "YOUR_SEARCH_DID_NOT_MATCH_ANY_ARTIST_NAMES_PLEASE_TRY_SEARCHING_AGAIN_OR_ADD_ARTIST"
      )}
      noDataDescTwo={t("PLEASE_TRY_SEARCHING_AGAIN_OR_ADD_ARTIST")}
      NoDataAddButtonTitle={t("ADD_ARTIST")}
      onTableFilterChange={onTableFilterChange}
      loaderHtml={loaderHtml}
      closeCreateArtistModal={closeCreateArtistModal}
      EmptyDataAddButton={openCreateArtistModal}
      placeHolderShimer={placeHolder}
      searchFilterClear={searchFilterClear}
      dimensions={dimensions}
      artistprofileValue={artistprofileValue}
      breadcrumbHeight={breadcrumbHeight}
      searchValue={searchValue}
      handleArtisttextChange={handleArtisttextChange}
      clearCreatData={clearCreatData}
      updateArtistFunction={(form) => {
        updateArtistFunction(form);
      }}
      loading={loading}
      onUpdateArtist={(e, item) => {
        onUpdateArtist(e, item);
      }}
      handleSubmitCreate={handleSubmitCreate}
      artistValue={artistValue}
      tableContainerRef={tableContainerRef}
      triggerRef={triggerRef}
      emptyDataDesc={t(
        "STILL_NO_ARTIST_HAS_BEEN_ADDED_PLEASE_CLICK_ON_THE_BELOW_BUTTON_TO_ADD_ARTIST"
      )}
      emptyDataDescTow={t("PLEASE_CLICK_ON_THE_BELOW_BUTTON_TO_ADD_ARTIST")}
      emptyTitle={t("NO_ARTIST_ADDED_YET")}
      emptyImg={CLOUDINARY_URL + "keyonont4puuheik5ydi.png"}
      setAboutTextEditorValue={setAboutTextEditorValue}
      aboutTextEditorValue={aboutTextEditorValue}
      handleUploadThumnailFile={handleUploadThumnailFile}
      handleClearUploadedThumnailFile={handleClearUploadedThumnailFile}
      uploadedDocumentThumnailFile={uploadedDocumentThumnailFile}
      profileImage={profileImage}
      tabHeaderHeight={tabHeaderHeight}
      itemsPerPage={pageSizeRef.current}
      deleteLoader={deleteLoader}
      aboutTouched={aboutTouched}
      setAboutTouched={setAboutTouched}
      filteredData={filteredData}
      instagramUrlValue={instagramUrlValue}
    />
  );
};

export default ArtistManagementContainer;
